'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAction } from 'next-safe-action/hooks';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { registerSchema, type RegisterInput } from '../types/schemas';
import { registerAction } from '../actions/register';
import { PasswordStrengthBar } from './password-strength-bar';

export function RegisterForm() {
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);

  const form = useForm<RegisterInput>({
    resolver: zodResolver(registerSchema),
    mode: 'onBlur',
    defaultValues: {
      organisationName: '',
      fullName: '',
      email: '',
      password: '',
    },
  });

  const watchedPassword = form.watch('password');

  const { execute, status } = useAction(registerAction, {
    onSuccess: ({ data }) => {
      if (!data) return;
      if (data.success) {
        const email = form.getValues('email');
        router.push(`/verify-email?email=${encodeURIComponent(email)}`);
      } else if (data.error === 'EMAIL_EXISTS') {
        form.setError('email', {
          message: 'An account with this email address already exists.',
        });
      }
    },
    onError: () => {
      toast.error(
        'Something went wrong creating your account. Please try again.',
      );
    },
  });

  const isExecuting = status === 'executing';

  function onSubmit(values: RegisterInput) {
    execute(values);
  }

  const emailValue = form.watch('email');

  return (
    <Form {...form}>
      {/* Already have account link */}
      <p className="text-sm font-sans text-muted-foreground mb-5">
        Already have an account?{' '}
        <Link
          href="/login"
          className="text-accent hover:text-accent/80 font-medium animated-underline transition-colors"
        >
          Sign in →
        </Link>
      </p>

      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        {/* Organisation Name */}
        <FormField
          control={form.control}
          name="organisationName"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="font-sans text-sm font-medium text-foreground">
                Organisation name
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder="e.g. Acme Industrial Ltd"
                  disabled={isExecuting}
                  className="font-sans"
                />
              </FormControl>
              <FormMessage className="text-destructive text-xs font-sans" />
            </FormItem>
          )}
        />

        {/* Full Name */}
        <FormField
          control={form.control}
          name="fullName"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="font-sans text-sm font-medium text-foreground">
                Your full name
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder="e.g. Jane Smith"
                  disabled={isExecuting}
                  className="font-sans"
                />
              </FormControl>
              <FormMessage className="text-destructive text-xs font-sans" />
            </FormItem>
          )}
        />

        {/* Email */}
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="font-sans text-sm font-medium text-foreground">
                Work email address
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  type="email"
                  placeholder="you@company.com"
                  disabled={isExecuting}
                  className="font-sans"
                />
              </FormControl>
              {/* EMAIL_EXISTS gets a custom error with a Sign in link; all other
                  errors go through standard FormMessage (which ignores children
                  when an error is set, so custom content must be outside it). */}
              {form.formState.errors.email?.message ===
              'An account with this email address already exists.' ? (
                <p className="text-destructive text-xs font-sans">
                  An account with this email address already exists.{' '}
                  <Link
                    href={`/login?email=${encodeURIComponent(emailValue)}`}
                    className="underline font-medium"
                  >
                    Sign in →
                  </Link>
                </p>
              ) : (
                <FormMessage className="text-destructive text-xs font-sans" />
              )}
            </FormItem>
          )}
        />

        {/* Password */}
        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="font-sans text-sm font-medium text-foreground">
                Password
              </FormLabel>
              <FormControl>
                <div className="relative">
                  <Input
                    {...field}
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Min. 8 characters"
                    disabled={isExecuting}
                    className="font-sans pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    tabIndex={-1}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </FormControl>
              <PasswordStrengthBar password={watchedPassword} />
              <FormMessage className="text-destructive text-xs font-sans" />
            </FormItem>
          )}
        />

        {/* Submit */}
        <div className="pt-2">
          <Button
            type="submit"
            disabled={isExecuting}
            className="w-full bg-accent text-accent-foreground hover:bg-accent/90 hover-glow-amber font-sans font-medium transition-all"
          >
            {isExecuting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating account…
              </>
            ) : (
              'Create account'
            )}
          </Button>
        </div>

        {/* Fine print */}
        <p className="text-[11px] font-sans text-muted-foreground text-center leading-relaxed">
          By creating an account you agree to our Terms of Service and Privacy
          Policy.
        </p>
      </form>
    </Form>
  );
}
